/* eslint-disable @typescript-eslint/naming-convention */
import { difference, isNumber } from 'lodash';
import { MAX_STAGES, type IExtraInfo, type ILayerProps, type IPosition, type ISize } from '@/version-types';
import type { IService } from '../../shared';
import { EdgelessBoundingBox } from '../canvas/bounding-box';
import { EventGenerator } from '../events';
import type { ServiceManager } from '../service-manager';

const MAX_PPT_COLUMNS = 5;
export const PPT_CONTAINER_PADDING = 160;

type TContainerBox = IPosition & ISize;
export type IPptContainerBox = TContainerBox & {
    taskId?: string;
    frameIds: number[];
};
type PptResult = PptResultContainer[];
type PptResultContainer = {
    taskId?: string;
    frames: PptResultFrame[];
};
type PptResultFrame = {
    page: number;
    frameId: number;
    layers: ILayerProps[];
};

export class PPTService implements IService {
    readonly pptWidth: number;
    readonly pptHeight: number;
    readonly gap: number;
    readonly containerPadding: number;
    /** PPT 容器位置，由 AgentLayoutService 按 taskId 排版后写入；为空时按当前 PPT frame 实时计算 */
    private __containerBoxes: IPptContainerBox[] = [];

    constructor(readonly service: ServiceManager) {
        const eventGenerator = new EventGenerator('edgeless:ppt:operate', this, service);
        this.pptWidth = 1920;
        this.pptHeight = 1080;
        this.gap = 200;
        this.containerPadding = PPT_CONTAINER_PADDING;
        console.log('edgeless:ppt:operate attrs ==>', eventGenerator.attrs);
    }

    onInit() {
        this.__containerBoxes = [];
    }

    onBindEvent() {
        this.service.frame.onAddExtraInfos((extraInfos) => {
            if (this.getEnable()) return;
            if (!extraInfos.some((extraInfo) => extraInfo?.isPPT)) return;

            this.setEnable(true);
        });

        // 添加、删除舞台后，更新 PPT frame 网格排版（仅在未启用 agent 自动排版时由 PPTService 自行排版）
        this.service.frame.onRerenderAll((renderFrameIds) => {
            if (!this.enable || this.service.agentLayout.getEnable()) return;
            requestAnimationFrame(() => {
                difference(renderFrameIds, [0])
                    .filter((frameId) => this.isPPTFrame(frameId))
                    .forEach((frameId, stageIndex, pptFrameIds) => {
                        const frame = this.service.frame.get(frameId);
                        if (!frame) return;
                        const newPos = this.getPosition(stageIndex, pptFrameIds);
                        const offsetLeft = newPos.left - frame.left;
                        const offsetTop = newPos.top - frame.top;
                        const renderIds = this.service.frame.getLayerIds(frameId);
                        this.service.flatLayer.traverseIds(false, renderIds, (layerId: string) => {
                            const layer = this.service.flatLayer.getLayer(layerId);
                            if (!layer) return;
                            layer.left += offsetLeft;
                            layer.top += offsetTop;
                        });
                        frame.update();
                    });
            });
        }, 900);
    }

    readonly enable = false;
    declare getEnable: () => boolean;
    declare setEnable: (enable: boolean) => void;
    declare onEnableChange: (fn: (enable: boolean) => void) => number;

    getResult(): PptResult;
    getResult(taskId: string | undefined): PptResultContainer | null;
    getResult(taskId?: string): PptResult | PptResultContainer | null {
        const shouldGetContainer = arguments.length > 0;
        if (!this.enable) return shouldGetContainer ? null : [];
        const defaultTaskId = this.__getDefaultTaskId();

        if (shouldGetContainer) {
            const targetTaskId = taskId || defaultTaskId;
            const frameIds = this.getPPTFrameIdsByTaskId(targetTaskId, defaultTaskId);

            return frameIds.length ? this.__getPPTResultContainer(targetTaskId, frameIds) : null;
        }

        return this.getPPTFrameGroups(defaultTaskId).map(({ taskId: groupTaskId, frameIds }) =>
            this.__getPPTResultContainer(groupTaskId, frameIds),
        );
    }

    getFrameSize(frameId?: number): ISize {
        if (!isNumber(frameId)) {
            return {
                width: this.pptWidth,
                height: this.pptHeight,
            };
        }

        const shape = this.service.frame.getShape(frameId);
        return {
            width: shape?.width || this.pptWidth,
            height: shape?.height || this.pptHeight,
        };
    }

    getPosition(stageIndex: number, frameIds: number[] = this.getPPTFrameIds()) {
        let left = 0;
        let top = 0;
        let rowHeight = 0;

        for (let index = 0; index < stageIndex; index++) {
            const size = this.getFrameSize(frameIds[index]);
            const isRowEnd = index % MAX_PPT_COLUMNS === MAX_PPT_COLUMNS - 1;

            rowHeight = Math.max(rowHeight, size.height);

            if (isRowEnd) {
                left = 0;
                top += rowHeight + this.gap;
                rowHeight = 0;
            } else {
                left += size.width + this.gap;
            }
        }

        return { left, top };
    }

    isPPTFrame(frameId: number) {
        return this.service.frame.getExtraInfoByFrameId(frameId).isPPT === true;
    }

    isLayerInPPTFrame(layerId: string) {
        const frameId = this.service.frame.getLayerFrameId(layerId);
        if (typeof frameId !== 'number') return false;
        return this.isPPTFrame(frameId);
    }

    getPPTFrameIds() {
        return this.service.frame.getRenderFrameIds().filter((frameId) => this.isPPTFrame(frameId));
    }

    getPPTFrameTaskId(frameId: number) {
        const taskId = this.service.frame.getExtraInfoByFrameId(frameId).extraInfo?.taskId;

        return typeof taskId === 'string' && taskId ? taskId : undefined;
    }

    getPPTExtraInfoTaskId(extraInfo?: IExtraInfo) {
        const taskId = extraInfo?.extraInfo?.taskId;

        return typeof taskId === 'string' && taskId ? taskId : undefined;
    }

    getPPTFrameGroupTaskId(frameId: number, defaultTaskId?: string) {
        return this.getPPTFrameTaskId(frameId) || defaultTaskId;
    }

    getPPTExtraInfoGroupTaskId(extraInfo?: IExtraInfo, defaultTaskId?: string) {
        return this.getPPTExtraInfoTaskId(extraInfo) || defaultTaskId;
    }

    getPPTFrameIdsByTaskId(taskId?: string, defaultTaskId?: string) {
        const targetKey = this.__getTaskKey(taskId);

        return this.getPPTFrameIds().filter((frameId) => {
            const frameTaskId = this.getPPTFrameGroupTaskId(frameId, defaultTaskId);

            return this.__getTaskKey(frameTaskId) === targetKey;
        });
    }

    getPPTFrameGroups(defaultTaskId?: string) {
        const groupMap = new Map<string, { taskId?: string; frameIds: number[] }>();

        this.getPPTFrameIds().forEach((frameId) => {
            const taskId = this.getPPTFrameGroupTaskId(frameId, defaultTaskId);
            const taskKey = this.__getTaskKey(taskId);
            let group = groupMap.get(taskKey);

            if (!group) {
                group = {
                    taskId,
                    frameIds: [],
                };
                groupMap.set(taskKey, group);
            }

            group.frameIds.push(frameId);
        });

        return Array.from(groupMap.values());
    }

    getPPTFramePage(frameId: number, defaultTaskId?: string) {
        const taskId = this.getPPTFrameGroupTaskId(frameId, defaultTaskId);
        const frameIds = this.getPPTFrameIdsByTaskId(taskId, defaultTaskId);
        const index = frameIds.indexOf(frameId);

        return index >= 0 ? index + 1 : 0;
    }

    getRemainingPPTFrameCount(taskId?: string, defaultTaskId?: string) {
        return MAX_STAGES - this.getPPTFrameIdsByTaskId(taskId, defaultTaskId).length;
    }

    canAddPPTFrames(count = 1, taskId?: string, defaultTaskId?: string) {
        return this.getRemainingPPTFrameCount(taskId, defaultTaskId) >= count;
    }

    canAddPPTFrameInfos(extraInfos: IExtraInfo[], defaultTaskId = this.__getDefaultTaskId()) {
        const addCountMap = new Map<string, { taskId?: string; count: number }>();

        extraInfos.forEach((extraInfo) => {
            if (!extraInfo?.isPPT) return;

            const taskId = this.getPPTExtraInfoGroupTaskId(extraInfo, defaultTaskId);
            const taskKey = this.__getTaskKey(taskId);
            const item = addCountMap.get(taskKey);

            if (item) {
                item.count += 1;
                return;
            }

            addCountMap.set(taskKey, {
                taskId,
                count: 1,
            });
        });

        return Array.from(addCountMap.values()).every(({ taskId, count }) =>
            this.canAddPPTFrames(count, taskId, defaultTaskId),
        );
    }

    getPPTFrameIndex(frameId: number) {
        return this.getPPTFrameIds().indexOf(frameId);
    }

    /** 设置单个 PPT 容器位置；传入 null 时清空已排版容器位置 */
    setContainerBox(box: TContainerBox | null) {
        this.__containerBoxes = box
            ? [
                  {
                      ...box,
                      frameIds: this.getPPTFrameIds(),
                  },
              ]
            : [];
    }

    setContainerBoxes(boxes: IPptContainerBox[]) {
        this.__containerBoxes = boxes.filter((box) => box.frameIds.length);
    }

    getContainerBoxes(defaultTaskId?: string): IPptContainerBox[] {
        const groups = this.getPPTFrameGroups(defaultTaskId);

        if (!groups.length) {
            this.__containerBoxes = [];
            return [];
        }

        return groups.map(({ taskId, frameIds }) => {
            const taskKey = this.__getTaskKey(taskId);
            const containerBox = this.__containerBoxes.find((box) => this.__getTaskKey(box.taskId) === taskKey);

            return {
                ...(containerBox || this.getContainer(frameIds)),
                taskId,
                frameIds,
            };
        });
    }

    getContainerBoxByTaskId(taskId?: string, defaultTaskId?: string) {
        const taskKey = this.__getTaskKey(taskId);

        return this.getContainerBoxes(defaultTaskId).find((box) => this.__getTaskKey(box.taskId) === taskKey) || null;
    }

    /**
     * 获取第一个 PPT 容器位置。
     * 优先使用排版写入的位置，否则按当前 PPT frame 实时计算；无 PPT frame 时不渲染容器。
     */
    getContainerBox(defaultTaskId?: string): IPptContainerBox | null {
        return this.getContainerBoxes(defaultTaskId)[0] || null;
    }

    setContainerToCenter(fitFromCenter?: boolean, fitAnimate?: boolean, forceRerender?: boolean) {
        const container = this.getContainerBox();
        if (!container) return;

        const box = new EdgelessBoundingBox(container.left, container.top, container.width, container.height);
        return this.service.edgelessCanvas.setObjectToCenter(box, fitFromCenter, fitAnimate, forceRerender);
    }

    setFrameContainerToCenter(frameId: number, fitFromCenter?: boolean, fitAnimate?: boolean, forceRerender?: boolean) {
        if (this.service.agentLayout.getEnable()) {
            this.service.agentLayout.arrangeBaseFrame();
        }

        const defaultTaskId = this.service.agentLayout.getEnable()
            ? this.service.agentLayout.getDefaultPPTTaskId()
            : undefined;
        const taskId = this.getPPTFrameGroupTaskId(frameId, defaultTaskId);
        const container = this.getContainerBoxByTaskId(taskId, defaultTaskId);
        if (!container) return;

        const box = new EdgelessBoundingBox(container.left, container.top, container.width, container.height);
        return this.service.edgelessCanvas.setObjectToCenter(box, fitFromCenter, fitAnimate, forceRerender);
    }

    setFramePPT(frameId: number, isPPT: boolean, taskId?: string) {
        const stageIndex = this.service.frame.frameIdToIndex(frameId);
        if (stageIndex < 0) return;

        const extraInfo: IExtraInfo = this.service.frame.getExtraInfoByFrameId(frameId);
        if (Boolean(extraInfo.isPPT) === isPPT && (!isPPT || this.getPPTFrameTaskId(frameId) === taskId)) return;

        if (isPPT) {
            this.service.frame.setExtraInfo(stageIndex, {
                ...extraInfo,
                isPPT: true,
                extraInfo: this.__getExtraInfoWithTaskId(extraInfo, taskId),
            });
            return;
        }

        this.service.frame.setExtraInfo(stageIndex, {
            ...extraInfo,
            isPPT: false,
            extraInfo: this.__getExtraInfoWithTaskId(extraInfo),
        });
    }

    syncFramePPTByContainer(frameId: number, container?: TContainerBox) {
        const targetContainer = this.getFrameIntersectContainer(frameId, container);
        const nextIsPPT = Boolean(targetContainer);
        const taskId =
            targetContainer && Object.prototype.hasOwnProperty.call(targetContainer, 'taskId')
                ? targetContainer.taskId
                : this.getPPTFrameTaskId(frameId);

        this.setFramePPT(frameId, nextIsPPT, taskId);
        return nextIsPPT;
    }

    alignFrameToPPTPosition(frameId: number): { frameId: number; offset: IPosition } | undefined {
        const stageIndex = this.getPPTFrameIndex(frameId);
        if (stageIndex < 0) return;

        const frame = this.service.frame.get(frameId);
        if (!frame) return;

        const newPos = this.getPosition(stageIndex);
        const offsetLeft = newPos.left - frame.left;
        const offsetTop = newPos.top - frame.top;
        if (!offsetLeft && !offsetTop) return;

        const renderIds = this.service.frame.getLayerIds(frameId);
        this.service.flatLayer.traverseIds(false, renderIds, (layerId: string) => {
            const layer = this.service.flatLayer.getLayer(layerId);
            if (!layer) return;
            layer.left += offsetLeft;
            layer.top += offsetTop;
        });
        frame.update();

        return {
            frameId,
            offset: {
                left: offsetLeft,
                top: offsetTop,
            },
        };
    }

    alignPPTFramesToPositions() {
        return this.getPPTFrameIds()
            .map((frameId) => this.alignFrameToPPTPosition(frameId))
            .filter(Boolean) as { frameId: number; offset: IPosition }[];
    }

    sortPPTFrameByPosition(frameId: number, defaultTaskId = this.__getDefaultTaskId()) {
        const taskId = this.getPPTFrameGroupTaskId(frameId, defaultTaskId);
        const pptFrameIds = this.getPPTFrameIdsByTaskId(taskId, defaultTaskId).filter((id) => id !== frameId);
        if (!pptFrameIds.length) return;

        const frameIdsWithoutCurrent = this.service.frame.getRenderFrameIds().filter((id) => id !== frameId);
        const targetFrameId = pptFrameIds[pptFrameIds.length - 1];
        const targetFrameIndex = frameIdsWithoutCurrent.indexOf(targetFrameId);
        const nextFrameIds = [...frameIdsWithoutCurrent];
        nextFrameIds.splice(targetFrameIndex + 1, 0, frameId);
        this.service.frame.setFrameIds(nextFrameIds);
    }

    getContainer(frameIds: number[] = this.getPPTFrameIds(), framePositions?: Map<number, IPosition>): TContainerBox {
        if (!frameIds.length) {
            const firstPosition = this.getPosition(0);
            return {
                left: firstPosition.left - this.containerPadding,
                top: firstPosition.top - this.containerPadding,
                width: this.pptWidth + this.containerPadding * 2,
                height: this.pptHeight + this.containerPadding * 2,
            };
        }

        let minLeft = Number.POSITIVE_INFINITY;
        let minTop = Number.POSITIVE_INFINITY;
        let maxRight = Number.NEGATIVE_INFINITY;
        let maxBottom = Number.NEGATIVE_INFINITY;

        frameIds.forEach((frameId) => {
            const shape = this.service.frame.getShape(frameId);
            const position = framePositions?.get(frameId);
            const left = position?.left ?? shape.left;
            const top = position?.top ?? shape.top;
            minLeft = Math.min(minLeft, left);
            minTop = Math.min(minTop, top);
            maxRight = Math.max(maxRight, left + shape.width);
            maxBottom = Math.max(maxBottom, top + shape.height);
        });

        const left = minLeft - this.containerPadding;
        const top = minTop - this.containerPadding;
        const width = maxRight - minLeft + this.containerPadding * 2;
        const height = maxBottom - minTop + this.containerPadding * 2;

        return {
            left,
            top,
            width,
            height,
        };
    }

    checkFrameInContainer(frameId: number, container?: TContainerBox) {
        return Boolean(this.getFrameIntersectContainer(frameId, container));
    }

    getFrameIntersectContainer(frameId: number, container?: TContainerBox): IPptContainerBox | null {
        if (container) {
            return this.__checkFrameInContainer(frameId, container)
                ? {
                      ...container,
                      frameIds: [],
                  }
                : null;
        }

        const defaultTaskId = this.service.agentLayout.getEnable()
            ? this.service.agentLayout.getDefaultPPTTaskId()
            : undefined;

        return (
            this.getContainerBoxes(defaultTaskId).find((item) => this.__checkFrameInContainer(frameId, item)) || null
        );
    }

    private __checkFrameInContainer(frameId: number, container: TContainerBox) {
        const shape = this.service.frame.getShape(frameId);
        const frameRight = shape.left + shape.width;
        const frameBottom = shape.top + shape.height;
        const containerRight = container.left + container.width;
        const containerBottom = container.top + container.height;

        return (
            shape.left < containerRight &&
            frameRight > container.left &&
            shape.top < containerBottom &&
            frameBottom > container.top
        );
    }

    private __getTaskKey(taskId?: string) {
        return taskId || '';
    }

    private __getDefaultTaskId() {
        return this.service.agentLayout.getEnable() ? this.service.agentLayout.getDefaultPPTTaskId() : undefined;
    }

    private __getPPTResultContainer(taskId: string | undefined, frameIds: number[]): PptResultContainer {
        return {
            taskId,
            frames: frameIds.map((frameId, index) => ({
                page: index + 1,
                frameId,
                layers: this.service.frame.getLayers(frameId),
            })),
        };
    }

    private __getExtraInfoWithTaskId(extraInfo: IExtraInfo, taskId?: string) {
        const nextExtraInfo = {
            ...extraInfo.extraInfo,
        };

        if (taskId) {
            nextExtraInfo.taskId = taskId;
        } else {
            delete nextExtraInfo.taskId;
        }

        return Object.keys(nextExtraInfo).length ? nextExtraInfo : undefined;
    }

    getGap(frameId: number) {
        if (frameId === 0) {
            return {
                left: -this.gap,
                top: 0,
                width: this.gap,
                height: this.getFrameSize().height,
            };
        }
        const shape = this.service.frame.getShape(frameId);
        return {
            ...shape,
            left: shape.left + shape.width,
            top: shape.top,
            width: this.gap,
            height: shape.height,
        };
    }

    checkGapInViewport(frameId: number) {
        const gap = this.getGap(frameId);
        const box = new EdgelessBoundingBox(gap.left, gap.top, gap.width, gap.height);

        const canvas = this.service.edgelessCanvas;
        const translateX = canvas.translateX;
        const translateY = canvas.translateY;

        return box.getIntersectsInfo(canvas.zoom, canvas.size, {
            x: translateX,
            y: translateY,
        });
    }

    checkContainerInViewport(container: TContainerBox) {
        const box = new EdgelessBoundingBox(container.left, container.top, container.width, container.height);

        const canvas = this.service.edgelessCanvas;
        const translateX = canvas.translateX;
        const translateY = canvas.translateY;

        return box.getIntersectsInfo(canvas.zoom, canvas.size, {
            x: translateX,
            y: translateY,
        });
    }
}
